// src/PlanWizard.jsx
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Step1ProgramType from './Step1ProgramType';
import Step2UserInfo from './Step2UserInfo';
import Step3FitnessGoal from './Step3FitnessGoal';
import Step4HealthInfo from './Step4HealthInfo';
import Step5EquipmentConfirm from './Step5EquipmentConfirm';
import Step6Measurements from './Step6Measurements';
import Step7Summary from './Step7Summary';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

export default function PlanWizard() {
  const navigate = useNavigate();
  const location = useLocation();

  const routeState =
    location?.state ||
    safeJsonParse(localStorage.getItem('planWizardState')) ||
    {};

  const campaignId = routeState?.campaignId ?? null;
  const campaignKey = routeState?.campaignKey ?? null;
  const campaignPrice = Number(routeState?.campaignPrice ?? 0);
  const priceMap = routeState?.prices ?? null;

  const initialStep = routeState?.initialStep ?? 1;
  const initialJourneyMode = routeState?.journeyMode ?? '';
  const initialRequestedPlan = routeState?.requestedPlan ?? '';
  const initialPlanType = routeState?.planType ?? 1;
  const initialPrice = routeState?.price ?? 0;
  const initialFree = !!routeState?.free;

  const [dark, setDark] = useState(true);
  const [step, setStep] = useState(initialStep);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    journeyMode: initialJourneyMode,
    requestedPlan: initialRequestedPlan,
    planType: initialPlanType,
    price: initialPrice,
    isFreeTrial: initialFree,

    gender: '',
    age: '',
    height: '',
    weight: '',

    goal: '',
    fitnessExperience: '',
    bodyFocus: '0',
    activityLevel: '',
    allergies: '',
    medicalConditions: '',

    equipment: '3',
    frequency: 3,

    measurements: {
      waist: '',
      arm: '',
      thigh: '',
      chest: '',
      hip: '',
    },
  });

  const colors = useMemo(() => {
    if (dark) {
      return {
        bg: '#070B14',
        bg2: '#08101C',
        bg3: '#0A1224',
        card: 'rgba(21,29,51,0.96)',
        card2: 'rgba(14,20,37,0.96)',
        border: 'rgba(255,255,255,0.08)',
        inputBg: '#10182A',
        text: '#FFFFFF',
        sub: '#B7C0D1',
        muted: '#8FA0BA',
        danger: '#ef4444',
      };
    }

    return {
      bg: '#F8FAFF',
      bg2: '#F2F6FF',
      bg3: '#EDF2FF',
      card: 'rgba(255,255,255,0.96)',
      card2: 'rgba(245,248,255,0.96)',
      border: 'rgba(15,23,42,0.10)',
      inputBg: '#FFFFFF',
      text: '#0F172A',
      sub: '#475569',
      muted: '#64748B',
      danger: '#ef4444',
    };
  }, [dark]);

  const totalSteps = 7;

  const isWorkoutOnly = Number(form.requestedPlan) === 1;
  const isDietOnly = Number(form.requestedPlan) === 2;
  const isSampleMode = form.journeyMode === 'sample';

  const setF = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setMeasurement = (key, value) => {
    setForm((prev) => ({
      ...prev,
      measurements: {
        ...prev.measurements,
        [key]: value,
      },
    }));
  };

  const selectJourneyMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      journeyMode: mode,
      planType: mode === 'sample' ? 0 : 1,
      isFreeTrial: false,
    }));
  };

  const selectRequestedPlan = (requestedPlan) => {
    const resolvedPrice = getRequestedPlanPrice(
      Number(requestedPlan),
      priceMap,
      campaignPrice
    );

    setForm((prev) => ({
      ...prev,
      requestedPlan,
      price: resolvedPrice,
      isFreeTrial: false,
    }));
  };

  const stepMeta = useMemo(
    () => ({
      1: {
        title: 'شروع مسیر',
        subtitle: 'روش شروع و نوع برنامه‌ات را انتخاب کن.',
      },
      2: {
        title: 'اطلاعات پایه',
        subtitle: 'سن، قد، وزن و جنسیت برای ساخت برنامه دقیق لازم است.',
      },
      3: {
        title: 'هدف و سابقه',
        subtitle: 'هدف، سابقه تمرینی و تمرکز بدنی را مشخص کن.',
      },
      4: {
        title: isWorkoutOnly
          ? 'سبک زندگی و سلامتی'
          : isDietOnly
          ? 'تغذیه و سلامتی'
          : 'سبک زندگی و سلامتی',
        subtitle: 'برای شخصی‌سازی بهتر، شرایط روزانه و محدودیت‌ها را وارد کن.',
      },
      5: {
        title: isDietOnly ? 'تعداد روزها' : 'تجهیزات و روزهای تمرین',
        subtitle: isDietOnly
          ? 'تعداد روزهای موردنظر برای برنامه‌ریزی را انتخاب کن.'
          : 'برنامه باید با امکانات واقعی تو قابل اجرا باشد.',
      },
      6: {
        title: 'اندازه‌ها',
        subtitle: 'این بخش اختیاری است و برای پیگیری بهتر تغییرات بدن استفاده می‌شود.',
      },
      7: {
        title: 'مرور نهایی',
        subtitle:
          form.planType === 0
            ? 'اطلاعاتت را مرور کن و نمونه برنامه را ببین.'
            : 'اطلاعاتت را مرور کن و درخواست برنامه را ثبت کن.',
      },
    }),
    [form.planType, isWorkoutOnly, isDietOnly]
  );

  const canNext = useMemo(() => {
    switch (step) {
      case 1:
        return !!form.journeyMode && !!form.requestedPlan && Number(form.price) > 0;

      case 2:
        return (
          !!form.gender &&
          !!String(form.age || '').trim() &&
          !!String(form.height || '').trim() &&
          !!String(form.weight || '').trim()
        );

      case 3:
        return form.goal !== '' && form.fitnessExperience !== '';

      case 4:
        if (isWorkoutOnly) {
          return form.activityLevel !== '' && form.medicalConditions !== '';
        }

        return (
          form.activityLevel !== '' &&
          form.allergies !== '' &&
          form.medicalConditions !== ''
        );

      case 5:
        if (isDietOnly) return !!form.frequency;
        return !!form.frequency && !!form.equipment && form.bodyFocus !== '';

      case 6:
      case 7:
        return true;

      default:
        return false;
    }
  }, [step, form, isWorkoutOnly, isDietOnly]);

  const progressPercent = `${(step / totalSteps) * 100}%`;

  const next = () => {
    setError('');

    if (step === 2 && !validateBaseInfo(form, setError)) return;

    if (step === 4 && isWorkoutOnly && form.allergies === '') {
      setF('allergies', '0');
    }

    if (step === 5 && isDietOnly && form.equipment === '') {
      setF('equipment', '3');
    }

    setStep((s) => Math.min(s + 1, totalSteps));
  };

  const back = () => {
    setError('');
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleFinish = async () => {
    if (form.planType === 0) {
      navigate('/plan-preview', {
        state: {
          form,
          campaignId,
          campaignKey,
        },
      });
      return;
    }

    await submit();
  };

  const submit = async () => {
    if (submitting) return;

    const token = localStorage.getItem('token');

    if (!token) {
      localStorage.setItem(
        'redirectAfterLogin',
        JSON.stringify({
          to: '/PlanWizard',
          state: {
            ...routeState,
            form,
            step,
          },
        })
      );

      navigate('/login');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

const res = await fetch(`${API_URL}/api/plan/create-android`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify(buildPayload(form, campaignId)),
});

      const data = await res.json();

      if (res.ok) {
navigate('/payment', {
  state: {
    planId: data?.planId,
    planRequestId: data?.planId,
    finalPrice: data?.price ?? 0,
    planTitle: data?.title ?? 'برنامه جدید',
    campaignId,
    campaignKey,
  },
});
        return;
      }

      setError(data?.Message || data?.message || 'ثبت درخواست ناموفق بود.');
    } catch (e) {
      setError(e?.message || 'مشکلی در ارتباط با سرور رخ داد.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div dir="rtl" className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg2}, ${colors.bg3})`,
          color: colors.text,
        }}
      >
        <div className="fixed top-20 right-[-80px] w-72 h-72 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
        <div className="fixed bottom-20 left-[-80px] w-72 h-72 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />

        <header className="relative z-20 max-w-5xl mx-auto px-4 pt-4">
          <div
            className="min-h-[64px] rounded-3xl border px-4 flex items-center justify-between backdrop-blur-xl shadow-lg"
            style={{
              backgroundColor: dark ? 'rgba(10,15,30,0.80)' : 'rgba(255,255,255,0.82)',
              borderColor: colors.border,
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="text-right"
            >
              <div className="font-black text-lg">ساخت برنامه</div>
              <div className="text-xs mt-1" style={{ color: colors.muted }}>
                مرحله {step} از {totalSteps}
              </div>
            </button>

            <button
              type="button"
              onClick={() => setDark((v) => !v)}
              className="w-11 h-11 rounded-2xl border flex items-center justify-center text-sm font-bold"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.inputBg,
                color: colors.text,
              }}
            >
              {dark ? 'روشن' : 'تاریک'}
            </button>
          </div>
        </header>

        <main className="relative z-10 max-w-5xl mx-auto px-4 py-6 pb-32">
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-right">
                <h1 className="text-2xl md:text-3xl font-black">
                  {stepMeta[step].title}
                </h1>
                <p className="mt-2 text-sm leading-7" style={{ color: colors.sub }}>
                  {stepMeta[step].subtitle}
                </p>
              </div>

              <div className="hidden sm:block text-sm font-bold" style={{ color: colors.muted }}>
                {Math.round((step / totalSteps) * 100)}٪
              </div>
            </div>

            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-l from-blue-600 to-purple-600 transition-all"
                style={{ width: progressPercent }}
              />
            </div>
          </div>

          <div
            className="rounded-[28px] border p-5 md:p-7 shadow-2xl backdrop-blur-xl"
            style={{
              background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
              borderColor: colors.border,
            }}
          >
{step === 1 && (
  <Step1ProgramType
    form={form}
    colors={colors}
    priceMap={priceMap}
    campaignPrice={campaignPrice}
    selectJourneyMode={selectJourneyMode}
    selectRequestedPlan={selectRequestedPlan}
  />
)}

{step === 2 && (
  <Step2UserInfo
    form={form}
    setF={setF}
    colors={colors}
  />
)}

{step === 3 && (
  <Step3FitnessGoal
    form={form}
    setF={setF}
    colors={colors}
  />
)}

{step === 4 && (
  <Step4HealthInfo
    form={form}
    setF={setF}
    colors={colors}
  />
)}

{step === 5 && (
  <Step5EquipmentConfirm
    form={form}
    setF={setF}
    colors={colors}
  />
)}

{step === 6 && (
  <Step6Measurements
    form={form}
    setMeasurement={setMeasurement}
    colors={colors}
  />
)}

{step === 7 && (
  <Step7Summary
    form={form}
    colors={colors}
  />
)}

            {error && (
              <div
                className="mt-5 rounded-2xl border px-4 py-3 text-sm font-bold text-center"
                style={{
                  borderColor: 'rgba(239,68,68,0.25)',
                  backgroundColor: dark ? 'rgba(127,29,29,0.20)' : 'rgba(254,242,242,0.96)',
                  color: colors.danger,
                }}
              >
                {error}
              </div>
            )}
          </div>
        </main>

        <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
          <div
            className="max-w-5xl mx-auto rounded-[26px] border p-3 shadow-2xl backdrop-blur-xl flex gap-2"
            style={{
              borderColor: colors.border,
              backgroundColor: dark ? 'rgba(8,12,22,0.92)' : 'rgba(255,255,255,0.92)',
            }}
          >
            {step > 1 && (
              <button
                type="button"
                onClick={back}
                disabled={submitting}
                className="w-32 rounded-2xl border px-4 py-3 font-bold disabled:opacity-60"
                style={{
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                بازگشت
              </button>
            )}

            <button
              type="button"
              disabled={!canNext || submitting}
              onClick={step < totalSteps ? next : handleFinish}
              className="flex-1 rounded-2xl px-4 py-3 text-white font-black disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
              }}
            >
              {step < totalSteps
                ? 'ادامه'
                : submitting
                ? 'در حال ثبت...'
                : form.planType === 0
                ? 'مشاهده نمونه برنامه'
                : 'ثبت درخواست برنامه'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* helpers */

function validateBaseInfo(form, setError) {
  const age = String(form.age || '').trim();
  const height = String(form.height || '').trim();
  const weight = String(form.weight || '').trim();

  if (!form.gender) {
    setError('لطفاً جنسیت را انتخاب کنید.');
    return false;
  }

  if (!age || !height || !weight) {
    setError('لطفاً سن، قد و وزن را کامل وارد کنید.');
    return false;
  }

  const ageNum = parseInt(age.replace(/\D/g, ''), 10);
  const heightNum = parseInt(height.replace(/\D/g, ''), 10);
  const weightNum = parseInt(weight.replace(/\D/g, ''), 10);

  if (!ageNum || ageNum < 18 || ageNum > 70) {
    setError('سن باید بین ۱۸ تا ۷۰ سال باشد.');
    return false;
  }

  if (!heightNum || heightNum < 100 || heightNum > 250) {
    setError('قد واردشده معتبر نیست.');
    return false;
  }

  if (!weightNum || weightNum < 25 || weightNum > 300) {
    setError('وزن واردشده معتبر نیست.');
    return false;
  }

  return true;
}

function buildPayload(form, campaignId) {
  return {
    RequestedPlan: Number(form.requestedPlan),
    PlanType: 1,
    Price: Number(form.price ?? 0),

    Age: parseInt(String(form.age || '').replace(/\D/g, ''), 10) || 0,
    Height: parseInt(String(form.height || '').replace(/\D/g, ''), 10) || 0,
    Weight: parseInt(String(form.weight || '').replace(/\D/g, ''), 10) || 0,

    Gender: parseInt(form.gender || 0, 10),
    Goal: parseInt(form.goal || 0, 10),
    FitnessExperience: parseInt(form.fitnessExperience || 0, 10),
    TrainingDaysPerWeek: Number(form.frequency || 0),

    ActivityLevel: form.activityLevel === '' ? null : Number(form.activityLevel),
    Allergies: form.allergies === '' ? null : Number(form.allergies),
    MedicalConditions:
      form.medicalConditions === '' ? null : Number(form.medicalConditions),
    EquipmentAccess: form.equipment === '' ? null : Number(form.equipment),
    BodyFocus: form.bodyFocus === '' ? null : Number(form.bodyFocus),

    Waist: toNullableNumber(form.measurements.waist),
    Arm: toNullableNumber(form.measurements.arm),
    Thigh: toNullableNumber(form.measurements.thigh),
    Chest: toNullableNumber(form.measurements.chest),
    Hip: toNullableNumber(form.measurements.hip),

    CampaignId: campaignId,
  };
}

function getRequestedPlanPrice(requestedPlan, priceMap, campaignPrice = 0) {
  const plan = Number(requestedPlan);

  if (plan === 3 && Number(campaignPrice) > 0) {
    return Number(campaignPrice);
  }

  const normal = priceMap?.normal ?? priceMap ?? null;

  switch (plan) {
    case 1:
      return extractNumericPrice(normal?.workout);
    case 2:
      return extractNumericPrice(normal?.diet);
    case 3:
      return extractNumericPrice(normal?.full);
    default:
      return 0;
  }
}

function extractNumericPrice(priceItem) {
  if (priceItem == null) return 0;
  if (typeof priceItem === 'number') return Number(priceItem);
  if (typeof priceItem === 'object') return Number(priceItem.price ?? 0);
  return 0;
}

function toNullableNumber(value) {
  const n = parseInt(String(value || '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}